import * as React from "react"
import * as HAN from "models/headAndNeck";
import { AppContext } from "AppContext";
import { ActionTypes } from "reducer/actions";
import { Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import ReportApi from "api/reportApi";
import * as fileHelper from "helpers/fileHelper";
import { FilterContext } from "models/core/filter";
import { HeadAndNeckIndicatorDefintionsUrl } from "config/appUrlConfig";
import { DashChip } from "components/common/DashChips";
import { Download, Downloading } from "@mui/icons-material";
import { Link } from "react-scroll";
import { getStateIndicatorReferences } from "api/dashboardApi";
import Loader from "pages/shared/pageloader";

enum HANIndicatorAction {
	UserClickedDownloadIndicator,
	DownloadIndicatorSuccess,
	DownloadIndicatorFail
}

type Action =
	{ type: HANIndicatorAction.UserClickedDownloadIndicator, payload: number }
	| { type: HANIndicatorAction.DownloadIndicatorSuccess }
	| { type: HANIndicatorAction.DownloadIndicatorFail }


interface State {
	indicator: number
}

const initialState: State = {
	indicator: 0
}

const reducer = (state: State, action: Action): State => {
	const { type } = action;

	switch (type) {
		case HANIndicatorAction.UserClickedDownloadIndicator:
			return { ...state, ...{ indicator: action.payload } };

		case HANIndicatorAction.DownloadIndicatorSuccess:
		case HANIndicatorAction.DownloadIndicatorFail:
			return { ...state, ...{ indicator: 0 } };

		default:
			return state;
	}
}

let stateWideData: {
	overall?: {
		title?: string;
		patients?: string;
		diagnosisToSurgeryLT30Days?: string;
		lengthOfStay?: string;
		mortality90Days?: string;
		twoYearSurvival?: string;
		diagnosisToIVSTLT45Days?: string;
		diagnosisToConcurrentCRTLT45Days?: string;
		diagnosisToRTLT45Days?: string;
		hadSurgery?: string;
		hadIVST?: string;
		hadRT?: string;
	},

	oralCavity?: {
		title?: string;
		patients?: string;
		diagnosisToSurgeryLT30Days?: string;
		lengthOfStay?: string;
		mortality90Days?: string;
		twoYearSurvival?: string;
		diagnosisToIVSTLT45Days?: string;
		diagnosisToConcurrentCRTLT45Days?: string;
		diagnosisToRTLT45Days?: string;
		hadSurgery?: string;
		hadIVST?: string;
		hadRT?: string;
	},

	oropharynx?: {
		title?: string;
		patients?: string;
		diagnosisToSurgeryLT30Days?: string;
		lengthOfStay?: string;
		mortality90Days?: string;
		twoYearSurvival?: string;
		diagnosisToIVSTLT45Days?: string;
		diagnosisToConcurrentCRTLT45Days?: string;
		diagnosisToRTLT45Days?: string;
		hadSurgery?: string;
		hadIVST?: string;
		hadRT?: string;
	},

	majorSalivaryGlands?: {
		title?: string;
		patients?: string;
		diagnosisToSurgeryLT30Days?: string;
		lengthOfStay?: string;
		mortality90Days?: string;
		twoYearSurvival?: string;
		diagnosisToIVSTLT45Days?: string;
		diagnosisToConcurrentCRTLT45Days?: string;
		diagnosisToRTLT45Days?: string;
		hadSurgery?: string;
		hadIVST?: string;
		hadRT?: string;
	},

	nasopharynx?: {
		title?: string;
		patients?: string;
		diagnosisToSurgeryLT30Days?: string;
		lengthOfStay?: string;
		mortality90Days?: string;
		twoYearSurvival?: string;
		diagnosisToIVSTLT45Days?: string;
		diagnosisToConcurrentCRTLT45Days?: string;
		diagnosisToRTLT45Days?: string;
		hadSurgery?: string;
		hadIVST?: string;
		hadRT?: string;
	},

	hypopharynx?: {
		title?: string;
		patients?: string;
		diagnosisToSurgeryLT30Days?: string;
		lengthOfStay?: string;
		mortality90Days?: string;
		twoYearSurvival?: string;
		diagnosisToIVSTLT45Days?: string;
		diagnosisToConcurrentCRTLT45Days?: string;
		diagnosisToRTLT45Days?: string;
		hadSurgery?: string;
		hadIVST?: string;
		hadRT?: string;
	},

	nasalCavityAndParanasalSinuses?: {
		title?: string;
		patients?: string;
		diagnosisToSurgeryLT30Days?: string;
		lengthOfStay?: string;
		mortality90Days?: string;
		twoYearSurvival?: string;
		diagnosisToIVSTLT45Days?: string;
		diagnosisToConcurrentCRTLT45Days?: string;
		diagnosisToRTLT45Days?: string;
		hadSurgery?: string;
		hadIVST?: string;
		hadRT?: string;
	},

	larynx?: {
		title?: string;
		patients?: string;
		diagnosisToSurgeryLT30Days?: string;
		lengthOfStay?: string;
		mortality90Days?: string;
		twoYearSurvival?: string;
		diagnosisToIVSTLT45Days?: string;
		diagnosisToConcurrentCRTLT45Days?: string;
		diagnosisToRTLT45Days?: string;
		hadSurgery?: string;
		hadIVST?: string;
		hadRT?: string;
	},

	otherPharynx?: {
		title?: string;
		patients?: string;
		diagnosisToSurgeryLT30Days?: string;
		lengthOfStay?: string;
		mortality90Days?: string;
		twoYearSurvival?: string;
		diagnosisToIVSTLT45Days?: string;
		diagnosisToConcurrentCRTLT45Days?: string;
		diagnosisToRTLT45Days?: string;
		hadSurgery?: string;
		hadIVST?: string;
		hadRT?: string;
	}

} = {};

export function HeadAndNeckDashboard() {
	const { state, dispatch } = React.useContext(AppContext);
	const [localState, localDispatch] = React.useReducer(reducer, initialState)

	const [showFilterChips, setFilterChips] = React.useState(true);

	//Set states to be able to switch between indicator views 
	const [viewBy, setViewBy] = React.useState(null);
	const [daysFromDiagToSurgInd, setDaysFromDiagToSurgInd] = React.useState(0);
	const [inHospitalMortalityInd, setInHospitalMortalityInd] = React.useState(0);
	const [thirtyDayMortalityInd, setThirtyDayMortalityInd] = React.useState(0);
	const [ninetyDayMortalityInd, setNinetyDayMortalityInd] = React.useState(0);
	const [oneYearSurvivalInd, setOneYearSurvivalInd] = React.useState(0);
	const [twoYearSurvivalInd, setTwoYearSurvivalInd] = React.useState(0);
	const [stage3AdjuvantTxInd, setStage3AdjuvantTxInd] = React.useState(0);
	const [neoAdjuvantXRTInd, setNeoAdjuvantXRTInd] = React.useState(0);

	const addFilter: HAN.AddFilterFunc = async (key: keyof typeof HAN.DashboardFilterKeys, value: string | string[], context?: FilterContext, labelOverride?: string) => {
		const filteredDashboard = await HAN.computeChartData(HAN.addFilter(state.page.dashboard as HAN.IHeadAndNeckDashboard)(key, value, context, labelOverride)); // state = what the other filter function returns 
		dispatch({ type: ActionTypes.UserClickedOnChart, payload: filteredDashboard });
	}

	const removeFilter: HAN.RemoveFilterFunc = async (key: keyof typeof HAN.DashboardFilterKeys, value?: string) => {

		const filteredDashboard = await HAN.computeChartData(HAN.removeFilter(state.page.dashboard as HAN.IHeadAndNeckDashboard, key, value));
		dispatch({ type: ActionTypes.UserClickedOnChart, payload: filteredDashboard });
	}

	React.useEffect(() => {
		dispatch({ type: ActionTypes.UserWantsToLoadMdtDashboard });
		const fetchStateReferenceData = async () => {
			stateWideData.overall = await getStateIndicatorReferences("head and neck - overall");
			stateWideData.oralCavity = await getStateIndicatorReferences("head and neck - oral cavity");
			stateWideData.oropharynx = await getStateIndicatorReferences("head and neck - oropharynx");
			stateWideData.majorSalivaryGlands = await getStateIndicatorReferences("head and neck - major salivary glands");
			stateWideData.nasopharynx = await getStateIndicatorReferences("head and neck - nasopharynx");
			stateWideData.hypopharynx = await getStateIndicatorReferences("head and neck - hypopharynx");
			stateWideData.nasalCavityAndParanasalSinuses = await getStateIndicatorReferences("head and neck - nasal cavity and paranasal sinuses");
			stateWideData.larynx = await getStateIndicatorReferences("head and neck - larynx");
			stateWideData.otherPharynx = await getStateIndicatorReferences("head and neck - other pharynx");
		}

		const loadDashboard = async () => {
			const value = await HAN.loadHeadAndNeckDashboard(state.page.dashboard as HAN.IHeadAndNeckDashboard);
			dispatch({ type: ActionTypes.UserLoadedMdtDashboard, payload: value });
		}

		Promise.all([fetchStateReferenceData(), loadDashboard()]);
		return () => {
			HAN.closeHeadAndNeckDashboard();
		}
	}, []);

	React.useEffect(() => {
		if (localState.indicator == 0)
			return;

		let ids = hanData['overall'].level1.filteredReferralIdCsv;
		let alldata = false;


		const getHeadAndNeckIndicatorPatientList = async () => {
			try {
				const data = await ReportApi.getHeadAndNeckIndicatorPatientList(ids, alldata, localState.indicator);
				if (data.errorMessage) {
					console.error(data.errorMessage);
					localDispatch({ type: HANIndicatorAction.DownloadIndicatorFail })
				}
				localDispatch({ type: HANIndicatorAction.DownloadIndicatorSuccess })
				return fileHelper.showFile(data.bytes, data.filename, fileHelper.fileTypeToMimeType(data.filetype), "exportPatientsElem")
			} catch {
				localDispatch({ type: HANIndicatorAction.DownloadIndicatorFail })
			}
		}

		getHeadAndNeckIndicatorPatientList();


	}, [localState.indicator]);

	if (state.page.loading || !state.page.dashboard.chartData)
		return <Loader />

	//Create function to show/hide filter chips
	const toggleChips = () => {
		if (showFilterChips) {
			setFilterChips(false);
		} else {
			setFilterChips(true);
		}
	};

	let activeFiltersArray = [];
	let filterArray = [];
	let chips = [];

	for (const key in state.page.dashboard.filters) {
		if (Object.prototype.hasOwnProperty.call(state.page.dashboard.filters, key)) {
			activeFiltersArray.push({ "key": key, "values": state.page.dashboard.filters[key].value, "parts": state.page.dashboard.filters[key].parts });
		}
	}

	for (const key in HAN.DashboardFilterKeys) {
		if (Object.prototype.hasOwnProperty.call(HAN.DashboardFilterKeys, key)) {
			filterArray.push({ "key": key, "label": HAN.DashboardFilterKeys[key].label });
		}
	}

	activeFiltersArray.forEach(a => {
		a.values.forEach(b => {
			chips.push(<DashChip key={a.key + b}
				removeFilterFn={removeFilter}
				filterName={filterArray.find(id => id.key == a.key).label}
				filterValue={b}
				filterId={a.key}
				filterParent={(a.parts.length > 1 ? a.parts[0] : null)}
			/>
			)
		})
	})

	const hanData = state.page.dashboard.chartData as HAN.IChartData;


	const downloadBtn = (extractTypeId: number) => {
		const { indicator } = localState;

		if (indicator == extractTypeId)
			return <Downloading />

		return (
			<Download style={{ cursor: "pointer" }} onClick={() =>
				localDispatch({ type: HANIndicatorAction.UserClickedDownloadIndicator, payload: extractTypeId })} />
		)
	}

	//Create function to title case the subtypes 
	function convertToTitleCase(str) {
		if (!str) {
			return "";
		}
		let strsplit = str.split(/(?=[A-Z])/);
		let strjoin = strsplit.join(" ");
		return strjoin.toLowerCase().replace(/\b\w/g, s => s.toUpperCase());
	}


	const ecog = {
		numerator: 0,
		denominator: 0,
		percentage: 0
	}

	const stage = {
		numerator: 0,
		denominator: 0,
		percentage: 0
	}

	//Create a set of the subtypes to be able to loop through 
	let cancerTypes = [
		'overall',
		'oralCavity',
		'oropharynx',
		'majorSalivaryGlands',
		'nasopharynx',
		'hypopharynx',
		'nasalCavityAndParanasalSinuses',
		'larynx',
		'otherPharynx'
	]

	let dashboard = [];

	//add shortcuts to the sections
	let shortcuts =
		<div key='header'
			style={{
				position: "sticky",
				top: "64px",
				left: "10px",
				zIndex: 2,
				backgroundColor: "white"
			}}>
			<Paper className="dash-header">

				<div>
					<Link activeClass="active" smooth spy={true} to="overall" offset={-130} style={{ paddingRight: "15px", color: "black" }}>
						Overall
					</Link>
					<Link activeClass="active" smooth spy={true} to="oralCavity" offset={-120} style={{ paddingRight: "15px", color: "black" }}>
						Oral Cavity
					</Link>
					<Link activeClass="active" smooth spy={true} to="oropharynx" offset={-120} style={{ paddingRight: "15px", color: "black" }}>
						Oropharynx
					</Link>
					<Link activeClass="active" smooth spy={true} to="majorSalivaryGlands" offset={-120} style={{ paddingRight: "15px", color: "black" }}>
						Major Salivary Glands
					</Link>
					<Link activeClass="active" smooth spy={true} to="nasopharynx" offset={-120} style={{ paddingRight: "15px", color: "black" }}>
						Nasopharynx
					</Link>
					<Link activeClass="active" smooth spy={true} to="hypopharynx" offset={-120} style={{ paddingRight: "15px", color: "black" }}>
						Hypopharynx
					</Link>
					<Link activeClass="active" smooth spy={true} to="nasalCavityAndParanasalSinuses" offset={-120} style={{ paddingRight: "15px", color: "black" }}>
						Nasal Cavity and Paranasal Sinuses
					</Link>
					<Link activeClass="active" smooth spy={true} to="larynx" offset={-120} style={{ paddingRight: "15px", color: "black" }}>
						Larynx
					</Link>
					<Link activeClass="active" smooth spy={true} to="otherPharynx" offset={-120} style={{ paddingRight: "15px", color: "black" }}>
						Other Pharynx
					</Link>
				</div>

			</Paper>


			{(showFilterChips ? <div style={{ marginLeft: "15px", marginBottom: "15px", marginTop: "10px", width: "80%" }}>
				{chips}
			</div> : null)}
		</div>

	dashboard.push(shortcuts);


	cancerTypes.forEach((subType, i) => {


		if (hanData[subType] && hanData[subType].level1.totalDiagnoses > 0) {

			ecog.numerator = hanData[subType].level1.totalDiagnoses - (hanData[subType].level2.ecogGroup.values[hanData[subType].level2.ecogGroup.labels.indexOf("Unk")] || 0);
			ecog.denominator = hanData[subType].level1.totalDiagnoses
			ecog.percentage = ecog.numerator / ecog.denominator * 100

			stage.numerator = hanData[subType].level1.totalDiagnoses - (hanData[subType].level2.tnmStage.values[hanData[subType].level2.tnmStage.labels.indexOf("Unk")] || 0);
			stage.denominator = hanData[subType].level1.totalDiagnoses;
			stage.percentage = stage.numerator / stage.denominator * 100
		} else {
			ecog.numerator = 0
			ecog.denominator = 0
			ecog.percentage = 0

			stage.numerator = 0
			stage.denominator = 0
			stage.percentage = 0
		}

		let itdashboard = <></>

		itdashboard = <div key={i} id={subType}>
			{/* <div style={{
			//position: "sticky",
			top: "64px",
			left: "10px",
			//zIndex: 2,
			backgroundColor: "white"
		}}>


			{(showFilterChips ? <div key={subType+chips} style={{ marginLeft: "15px", marginBottom: "15px", marginTop: "10px", width: "80%" }}>
				{chips}
			</div> : null)}
		</div> */}



			<div style={{
				maxWidth: 1000,
				margin: "0 auto"
			}}>
				<div style={{
					textAlign: "right",
					paddingBottom: "10px",
					paddingTop: "10px",
					//position: "sticky",
					//zIndex: 1,
					top: "60px",
					backgroundColor: "white"
				}}>
					<Button onClick={() => localDispatch({ type: HANIndicatorAction.UserClickedDownloadIndicator, payload: i + 1 })}
						style={{
							backgroundColor: (hanData[subType].level1.totalDiagnoses > 0 ? ("#1e88e5") : ("#d3d3d3")),
							color: "white",
							alignItems: "right"
						}}>
						Download Patient Details
					</Button>
				</div>

				<TableContainer component={Paper}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell colSpan={4}>
									<b>{convertToTitleCase(subType)}</b>
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>Indicator</TableCell>
								<TableCell align="center">My MDT(s)*</TableCell>
								<TableCell align="center">{stateWideData[subType].title}^</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							<TableRow>
								<TableCell>Head and neck cancer diagnoses^</TableCell>
								<TableCell align="center">{`${hanData[subType].level1.totalDiagnoses}`}</TableCell>
								<TableCell align="center">{stateWideData[subType].patients}</TableCell>
							</TableRow>

							<TableRow>
								<TableCell>Percentage of patients with ECOG</TableCell>
								<TableCell align="center">{`${ecog.percentage.toFixed(2)}%`} <br /> {`(${ecog.numerator}/${ecog.denominator})`}</TableCell>
								<TableCell align="center">-</TableCell>
							</TableRow>

							<TableRow>
								<TableCell>Percentage of patients with Stage</TableCell>
								<TableCell align="center">{`${stage.percentage.toFixed(2)}%`} <br /> {`(${stage.numerator}/${stage.denominator})`}</TableCell>
								<TableCell align="center">-</TableCell>
							</TableRow>

							<TableRow>
								<TableCell colSpan={4}>
									<b>Surgical</b>
								</TableCell>
							</TableRow>

							<TableRow>
								<TableCell>Received surgery</TableCell>
								<TableCell align="center">{`${hanData[subType].level1.hadSurgery.percentage.toFixed(2)}%`} <br /> {`(${hanData[subType].level1.hadSurgery.numerator}/${hanData[subType].level1.hadSurgery.denominator})`}</TableCell>
								<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData[subType].hadSurgery}</TableCell>
							</TableRow>

							<TableRow>
								<TableCell>Received surgery as first treatment within 30 days of diagnosis?</TableCell>
								<TableCell align="center">{`${hanData[subType].level1.diagnosisToSurgeryLT30Days.percentage.toFixed(2)}%`} <br /> {`(${hanData[subType].level1.diagnosisToSurgeryLT30Days.numerator}/${hanData[subType].level1.diagnosisToSurgeryLT30Days.denominator})`}</TableCell>
								<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData[subType].diagnosisToSurgeryLT30Days}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>Hospital length of stay</TableCell>
								<TableCell align="center">{`${hanData[subType].level1.lengthOfStay.median}`} <br /> {`(${hanData[subType].level1.lengthOfStay.min} - ${hanData[subType].level1.lengthOfStay.max})`}</TableCell>
								<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData[subType].lengthOfStay}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>90-day mortality post surgery</TableCell>
								<TableCell align="center">{`${hanData[subType].level1.mortality90Days.percentage.toFixed(2)}%`} <br /> {`(${hanData[subType].level1.mortality90Days.numerator}/${hanData[subType].level1.mortality90Days.denominator})`}</TableCell>
								<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData[subType].mortality90Days}</TableCell>
							</TableRow>

							<TableRow>
								<TableCell>2-year surgical survival</TableCell>
								<TableCell align="center">{`${hanData[subType].level1.twoYearSurvival.percentage.toFixed(2)}%`} <br /> {`(${hanData[subType].level1.twoYearSurvival.numerator}/${hanData[subType].level1.twoYearSurvival.denominator})`}</TableCell>
								<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData[subType].twoYearSurvival}</TableCell>
							</TableRow>

							<TableRow>
								<TableCell colSpan={4}>
									<b>Systemic Therapy</b>
								</TableCell>
							</TableRow>

							<TableRow>
								<TableCell>Received IV systemic therapy</TableCell>
								<TableCell align="center">{`${hanData[subType].level1.hadIVST.percentage.toFixed(2)}%`} <br /> {`(${hanData[subType].level1.hadIVST.numerator}/${hanData[subType].level1.hadIVST.denominator})`}</TableCell>
								<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData[subType].hadIVST}</TableCell>
							</TableRow>

							<TableRow>
								<TableCell>Time from diagnosis to first IV systemic therapy within 45 days</TableCell>
								<TableCell align="center">{`${hanData[subType].level1.diagnosisToIVSTLT45Days.percentage.toFixed(2)}%`} <br /> {`(${hanData[subType].level1.diagnosisToIVSTLT45Days.numerator}/${hanData[subType].level1.diagnosisToIVSTLT45Days.denominator})`}</TableCell>
								<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData[subType].diagnosisToIVSTLT45Days}</TableCell>
							</TableRow>

							<TableRow>
								<TableCell>Time from diagnosis to first concurrent CRT within 45 days</TableCell>
								<TableCell align="center">{`${hanData[subType].level1.diagnosisToConcurrentCRTLT45Days.percentage.toFixed(2)}%`} <br /> {`(${hanData[subType].level1.diagnosisToConcurrentCRTLT45Days.numerator}/${hanData[subType].level1.diagnosisToConcurrentCRTLT45Days.denominator})`}</TableCell>
								<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData[subType].diagnosisToConcurrentCRTLT45Days}</TableCell>
							</TableRow>

							<TableRow>
								<TableCell colSpan={4}>
									<b>Radiation Therapy</b>
								</TableCell>
							</TableRow>

							<TableRow>
								<TableCell>Received radiation therapy</TableCell>
								<TableCell align="center">{`${hanData[subType].level1.hadRT.percentage.toFixed(2)}%`} <br /> {`(${hanData[subType].level1.hadRT.numerator}/${hanData[subType].level1.hadRT.denominator})`}</TableCell>
								<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData[subType].hadRT}</TableCell>
							</TableRow>

							<TableRow>
								<TableCell>Time from diagnosis to first radiation therapy within 45 days</TableCell>
								<TableCell align="center">{`${hanData[subType].level1.diagnosisToRTLT45Days.percentage.toFixed(2)}%`} <br /> {`(${hanData[subType].level1.diagnosisToRTLT45Days.numerator}/${hanData[subType].level1.diagnosisToRTLT45Days.denominator})`}</TableCell>
								<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData[subType].diagnosisToRTLT45Days}</TableCell>
							</TableRow>

						</TableBody>
					</Table>
				</TableContainer>

				<div style={{ fontSize: "small", textAlign: "left" }}>
				<p><i>*Data based on facility of MDT</i> </p>
				</div>

				<div style={{ fontSize: "small", textAlign: "left" }}>
					<p><i>^The comparison data in the Qld report is based on a person's incidence case of the cancer. The MDT data is all diagnoses (with a diagnosis date) presented to MDT using the Qool application</i> </p>
				</div>

				<div style={{ textAlign: "center" }}>
					<p>This report is based on the <a href={HeadAndNeckIndicatorDefintionsUrl} target="_blank">Queensland Head and Neck Cancer Quality Index 2015-2019.</a><br />Indicator definitions and glossary can be found here also</p>
				</div>

			</div>
			<a id="exportPatientsElem" style={{ display: "none" }}></a>

		</div>

		dashboard.push(itdashboard);

	})

	return (
		<div>{dashboard}</div>
	)


} 